/*
 * Copyright (c) 2025 Cozy Intellect Inc.
 * File Path: undefined
 */

// TODO: Move to sharedlib

/*
assistantState: Readable<State<AssistantContext, AssistantEvents, any, {   value: any   context: AssistantContext }, TypegenDisabled>
 */
import type { Memory, Transcript, TranscriptInfo } from '@repo/common';
import { chatServiceSections } from '@repo/sharedlib/src/chat.service.store';

import { assistantState } from '$lib/util/assistant';
import type { RecordingStatus } from '@repo/sharedlib/src/stores/meeting';
import { ensureTranscriptFull } from '@repo/sharedlib/src/stores/transcripts';
import { viewer } from '@repo/sharedlib/src/stores/viewer';
import { isTranscriptFull } from '@repo/toolslib/src/utils/transcript.util';
import { derived, type Readable } from 'svelte/store';

function hasSufficientAudio(transcript?: Transcript) {
	// Example "sufficient audio" check. Adjust logic as needed.
	return transcript?.fileExists && (transcript?.duration || 0) > 1;
}

function hasSufficientText(transcript?: Transcript) {
	if (!isTranscriptFull(transcript)) {
		return false;
	}
	// Example "sufficient transcription text" check. Adjust logic as needed.
	return transcript?.rawOutput && transcript.rawOutput.length > 0;
}

function hasSufficientUserNotes(transcript?: Transcript) {
	if (!isTranscriptFull(transcript)) {
		return false;
	}
	// Check if user notes have enough content to create a summary
	return transcript?.userNotes && transcript.userNotes.trim().length > 1;
}

function hasSummary(transcript?: Transcript) {
	return !!transcript?.summary;
}

export const assistantDerived: Readable<TranscriptInfo> = derived(
	[assistantState, viewer, chatServiceSections],
	([$assistantState, $viewer, _$chatServiceSections]): TranscriptInfo => {
		// Identify overall machine states.
		const isRecording =
			$assistantState.matches('assistant.recorder.recording') ||
			$assistantState.matches('assistant.recorder.spawning');
		const isTranscribing =
			$assistantState.matches('assistant.transcriber.transcribing') ||
			$assistantState.matches('assistant.transcriber.dedupe');
		const isFinalTranscribing = $assistantState.matches('assistant.transcriber.finalTranscribing');
		const isCanceling = $assistantState.matches('assistant.transcriber.canceling');
		const isSummarizing =
			$assistantState.matches('assistant.summarizer.summarizing') ||
			$assistantState.matches('assistant.summarizer.finalSummarizing');
		const isStopping =
			$assistantState.matches('assistant.recorder.stopping') ||
			$assistantState.matches('assistant.transcriber.stopping') ||
			$assistantState.matches('assistant.summarizer.stopping');
		const isAborting = $assistantState.matches('assistant.recorder.aborting');
		const isConvertingWav = $assistantState.matches('assistant.recorder.convertingWav');

		// The "assistant transcript" loaded by the machine.
		// IMPORTANT: Use $assistantState.context to get the current reactive context
		const assistantTranscript = $assistantState.context.transcript;
		const assistantTranscriptLoaded = Boolean(assistantTranscript);
		const audioDetected = isRecording ? $assistantState.context.audioDetected : undefined;
		const deviceName = $assistantState.context.deviceName;

		// The transcript in the viewer's active Memory (if any).
		let viewerMemory: Memory | undefined;
		let viewerTranscript: Transcript | undefined;
		let viewerTranscriptLoaded: boolean;

		if ($viewer.activeType === 'memory' && $viewer.active) {
			viewerMemory = $viewer.active as Memory;
			viewerTranscript = $viewer.activeTranscript;
			try {
				viewerTranscript = ensureTranscriptFull(viewerTranscript, 'viewerTranscript');
				viewerTranscriptLoaded = true;
			} catch (error) {
				// This will throw if the transcript is not full
				console.debug('[Memory Store] Error ensuring transcript full:', error);
				viewerTranscriptLoaded = false;
			}
		} else {
			viewerTranscriptLoaded = false;
		}

		// Is the machine transcript the same as the one being viewed?
		// Adjust based on your data model (id, uniqueId, etc.).
		const isSameTranscript =
			(assistantTranscript && viewerTranscript && assistantTranscript.id === viewerTranscript.id) ||
			false;

		// "Can" checks: used by the UI to enable/disable buttons.
		const canUnloadTranscript =
			assistantTranscriptLoaded &&
			!isRecording &&
			!isTranscribing &&
			!isFinalTranscribing &&
			!isCanceling &&
			!isSummarizing;
		const assistantAvailable =
			!assistantTranscriptLoaded || canUnloadTranscript || isSameTranscript;

		const hasSufficentTranscript = hasSufficientText(viewerTranscript);
		const hasSufficentUserNotes = hasSufficientUserNotes(viewerTranscript);
		const hasSufficentViewerAudio = hasSufficientAudio(viewerTranscript);
		const hasSufficientAssistantAudio = $assistantState.context.transcribeProgress > 5;

		// Check if this is a pre-meeting state (has calendar event and meeting hasn't started)
		const isPreMeeting =
			viewerTranscript?.eventId &&
			!isRecording &&
			!isTranscribing &&
			!isFinalTranscribing &&
			!hasSufficentTranscript;

		const canStartRecording =
			viewerTranscriptLoaded &&
			!isRecording &&
			!isTranscribing &&
			!isFinalTranscribing &&
			!isCanceling &&
			!isSummarizing &&
			!hasSufficentTranscript;
		const canStopRecording = isRecording && !isStopping;
		const canStartTranscribing =
			(!isRecording && !isTranscribing && !isFinalTranscribing && !isCanceling && !isSummarizing) ||
			false;
		const canStopTranscribing = (isTranscribing || isFinalTranscribing) && !isStopping;
		const canStartSummarizing =
			(!isRecording &&
				!isTranscribing &&
				!isFinalTranscribing &&
				!isCanceling &&
				!isSummarizing &&
				(hasSufficentTranscript || hasSufficentUserNotes)) ||
			false;
		const canStopSummarizing = isSummarizing && !isStopping;
		const canExportTranscript = !!hasSufficentTranscript;

		// Example logic if you also want separate "delete allowed" or "edit allowed" flags:
		// e.g., disallow delete if the transcript is currently loaded in the machine
		const canDeleteViewerTranscript = !isSameTranscript || canUnloadTranscript;
		// Typically, editing is always allowed, but you can add your own concurrency checks:
		const canEditViewerTranscript = true; // or !isSummarizing, etc.

		// Check if the assistant machine can respond to DELETE_RECORDINGS
		const canDeleteRecordings =
			$assistantState.matches('selectFile.ready') ||
			$assistantState.matches('assistant.recorder.ready') ||
			$assistantState.matches('assistant.recorder.complete') ||
			$assistantState.matches('assistant.recorder.error') ||
			$assistantState.matches('assistant') ||
			$assistantState.matches('selectFile.awaitingSetup');

		// Debug hasSummary calculation
		const hasSummaryResult = hasSummary(viewerTranscript);

		const recordProgress = $assistantState.context.recordProgress;

		return {
			// Machine "busy" checks
			isRecording,
			recordProgress,
			isStopping,
			isAborting,
			isConvertingWav,
			isTranscribing,
			isFinalTranscribing,
			isCanceling,
			isSummarizing,
			audioDetected,
			deviceName,
			// Machine transcript info
			assistantTranscript,
			assistantTranscriptLoaded,
			// Viewer transcript info
			viewerMemory,
			viewerTranscript,
			viewerTranscriptLoaded,
			isSameTranscript,
			assistantAvailable,

			// UI logic: what the user can do
			canUnloadTranscript,
			canStartRecording,
			canStopRecording,
			canStartTranscribing,
			canStopTranscribing,
			canStartSummarizing,
			canStopSummarizing,
			canDeleteViewerTranscript,
			canEditViewerTranscript,
			canExportTranscript,
			canDeleteRecordings,
			hasSufficientText: hasSufficientText(viewerTranscript) || false,
			hasSufficientUserNotes: hasSufficientUserNotes(viewerTranscript) || false,
			hasSufficentViewerAudio: hasSufficentViewerAudio || false,
			hasSufficientAssistantAudio: hasSufficientAssistantAudio || false,
			hasSummary: hasSummaryResult || false,
			summarizeLabel:
				isTranscriptFull(viewerTranscript) && viewerTranscript?.meeting?.minutes?.summary
					? 'Regenerate notes'
					: 'Generate notes',
			isPreMeeting: isPreMeeting || false
		};
	}
);

export const getMemoryIdByTranscriptId = (transcriptId: string) => {
	// TODO: Add lookup logic in case we ever have multiple memories for the same transcript
	return transcriptId;
};

// Real recording status store that maps assistantDerived states to RecordingStatus
export const realRecordingStatus = derived(
	[assistantDerived],
	([$assistantDerived]): RecordingStatus => {
		// Map assistant states to recording status
		if ($assistantDerived.isRecording) {
			return 'recording';
		} else if ($assistantDerived.isAborting) {
			return 'deleting';
		} else if ($assistantDerived.isStopping) {
			return 'saving';
		} else if (
			$assistantDerived.isTranscribing ||
			$assistantDerived.isFinalTranscribing ||
			$assistantDerived.isSummarizing
		) {
			// Consider transcribing/summarizing as "saving" state
			return 'saving';
		} else {
			return 'idle';
		}
	}
);

// Function to connect the real recording status store to the sharedlib store
export function connectRealRecordingStatus() {
	// This function can be called to properly connect the stores
	// For now, the realRecordingStatus is already properly derived from assistantDerived
	return realRecordingStatus;
}
